import { useMemo, useState } from 'react';
import { DAY_NAMES } from '../types';
import type { EventInstance } from '../types';
import { buildMonthGrid, getWeekNumber, parseDateKey, startOfDay } from '../utils';
import { useIsNarrowViewport } from '../hooks/useIsNarrowViewport';
import { MonthCell } from './MonthCell';

interface MonthViewProps {
  currentDate: Date;
  events: EventInstance[];
  weekStartDay: number;
  onClickDate: (date: Date) => void;
  /** A drag across days picked out this inclusive span. */
  onSelectDays: (from: Date, to: Date) => void;
  /** The user asked to see the events a cell had no room for. */
  onShowMore: (date: Date) => void;
  onClickEvent: (event: EventInstance) => void;
  showWeekends?: boolean;
  monthViewMaxEvents?: number;
  showEventTimesInMonthView?: boolean;
  showWeekNumbers?: boolean;
  dimPastEvents?: boolean;
  eventColorPriority?: 'event' | 'calendar';
  onEventDragStart?: (eventId: string, e: React.MouseEvent) => void;
  draggingEventId?: string | null;
}

// Touch only: hold this long to start selecting days; anything shorter is a
// tap and any movement first is a scroll. Mirrors the time grid.
const LONG_PRESS_MS = 400;
const MOVE_CANCEL_THRESHOLD_PX = 5;

// Floor for a week row. A phone in landscape is only ~375px tall, where six
// 100px rows put most of the month below the fold.
const MIN_ROW_HEIGHT_PX = 100;
const MIN_ROW_HEIGHT_NARROW_PX = 64;

/** The day cell under a point, for a drag that has left its starting cell. */
function dayAtPoint(x: number, y: number): Date | null {
  const el = document.elementFromPoint(x, y);
  const cell = el?.closest<HTMLElement>('[data-calendar-date]');
  return cell?.dataset.calendarDate ? parseDateKey(cell.dataset.calendarDate) : null;
}

export function MonthView({
  currentDate,
  events,
  weekStartDay,
  onClickDate,
  onSelectDays,
  onShowMore,
  onClickEvent,
  showWeekends = true,
  monthViewMaxEvents = 3,
  showEventTimesInMonthView = false,
  showWeekNumbers = false,
  dimPastEvents = false,
  eventColorPriority = 'event',
  onEventDragStart,
  draggingEventId,
}: MonthViewProps) {
  const weeks = buildMonthGrid(currentDate, events, weekStartDay);

  // Weekend column indices (relative to weekStartDay ordering)
  const weekendIndices = useMemo(() => {
    if (showWeekends) return null;
    const indices: number[] = [];
    for (let i = 0; i < 7; i++) {
      const dayIndex = (weekStartDay + i) % 7;
      if (dayIndex === 0 || dayIndex === 6) indices.push(i);
    }
    return indices;
  }, [weekStartDay, showWeekends]);

  const dayHeaders = [];
  for (let i = 0; i < 7; i++) {
    const dayIndex = (weekStartDay + i) % 7;
    if (weekendIndices?.includes(i)) continue;
    dayHeaders.push(DAY_NAMES[dayIndex]);
  }

  const colCount = dayHeaders.length;
  // A fixed leading column for the ISO week number, when the user wants it.
  const gridColumns = `${showWeekNumbers ? 'minmax(2rem, auto) ' : ''}repeat(${colCount}, minmax(0, 1fr))`;

  // Rows share the available height equally so every cell is the same size
  // regardless of its event count. The one exception is the "show all events"
  // setting (monthViewMaxEvents === 0), where a cell is allowed to grow —
  // otherwise the events the user asked to see would be clipped away.
  const isNarrow = useIsNarrowViewport();
  const minRowHeightPx = isNarrow ? MIN_ROW_HEIGHT_NARROW_PX : MIN_ROW_HEIGHT_PX;
  const showAllEvents = monthViewMaxEvents === 0;
  const rowSizing = showAllEvents
    ? `minmax(${minRowHeightPx}px, auto)`
    : `minmax(${minRowHeightPx}px, 1fr)`;

  const [selection, setSelection] = useState<{ anchor: Date; current: Date } | null>(null);
  const selectedRange = selection
    ? [
        Math.min(startOfDay(selection.anchor).getTime(), startOfDay(selection.current).getTime()),
        Math.max(startOfDay(selection.anchor).getTime(), startOfDay(selection.current).getTime()),
      ]
    : null;

  /**
   * Day selection, delegated from the week container so a drag can cross cells.
   *
   * Mouse: press anchors, drag extends, release creates. Touch: a tap creates
   * on the tapped day, a plain drag scrolls the month, and only a deliberate
   * hold starts a selection — the same contract as the time grid, so the
   * gesture a user learns in one view works in the other.
   */
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    const target = e.target as HTMLElement;
    // Event chips are buttons and own their own click.
    if (target.closest('button')) return;

    const cell = target.closest<HTMLElement>('[data-calendar-date]');
    const anchor = cell?.dataset.calendarDate ? parseDateKey(cell.dataset.calendarDate) : null;
    if (!anchor) return;

    const isMouse = e.pointerType === 'mouse';
    const startX = e.clientX;
    const startY = e.clientY;

    let phase: 'pending' | 'dragging' | 'done' = isMouse ? 'dragging' : 'pending';
    let current = anchor;
    let moved = false;

    if (phase === 'dragging') setSelection({ anchor, current });

    const blockTouchScroll = (te: TouchEvent) => {
      if (phase === 'dragging') te.preventDefault();
    };

    const timer = window.setTimeout(() => {
      if (phase !== 'pending') return;
      phase = 'dragging';
      setSelection({ anchor, current });
    }, LONG_PRESS_MS);

    const onMove = (mv: PointerEvent) => {
      if (phase === 'done') return;

      if (phase === 'pending') {
        if (
          Math.abs(mv.clientX - startX) < MOVE_CANCEL_THRESHOLD_PX &&
          Math.abs(mv.clientY - startY) < MOVE_CANCEL_THRESHOLD_PX
        ) {
          return;
        }
        window.clearTimeout(timer);
        phase = 'done';
        cleanup();
        return;
      }

      const over = dayAtPoint(mv.clientX, mv.clientY);
      if (!over || startOfDay(over).getTime() === startOfDay(current).getTime()) return;
      current = over;
      moved = true;
      setSelection({ anchor, current });
    };

    const onUp = () => {
      const endedIn = phase;
      cleanup();
      if (endedIn === 'done') return;

      const spansOneDay = startOfDay(anchor).getTime() === startOfDay(current).getTime();
      if (endedIn === 'pending' || !moved || spansOneDay) {
        onClickDate(anchor);
        return;
      }
      const [from, to] = anchor <= current ? [anchor, current] : [current, anchor];
      onSelectDays(from, to);
    };

    const cleanup = () => {
      window.clearTimeout(timer);
      phase = 'done';
      setSelection(null);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', cleanup);
      window.removeEventListener('touchmove', blockTouchScroll);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', cleanup);
    window.addEventListener('touchmove', blockTouchScroll, { passive: false });
  };

  return (
    <div className="flex flex-col flex-1 overflow-auto">
      {/* Day-of-week header */}
      <div className="grid border-b" style={{ gridTemplateColumns: gridColumns }}>
        {showWeekNumbers && (
          <div className="border-r py-2 text-center text-[10px] font-medium uppercase text-muted-foreground">
            Wk
          </div>
        )}
        {dayHeaders.map((name) => (
          <div
            key={name}
            className="text-center text-xs font-medium text-muted-foreground py-2 border-r last:border-r-0 truncate"
          >
            {name}
          </div>
        ))}
      </div>

      {/* Week rows */}
      <div
        className="flex-1 grid select-none"
        style={{ gridTemplateRows: `repeat(${weeks.length}, ${rowSizing})` }}
        onPointerDown={handlePointerDown}
      >
        {weeks.map((week, wi) => {
          const cells = weekendIndices
            ? week.filter((_, di) => !weekendIndices.includes(di))
            : week;

          return (
            <div key={wi} className="grid" style={{ gridTemplateColumns: gridColumns }}>
              {showWeekNumbers && cells[0] && (
                // Labelled, because a bare number in an unheaded column tells a
                // screen reader nothing.
                <div
                  aria-label={`Week ${getWeekNumber(cells[0].date)}`}
                  className="flex items-start justify-center border-b border-r px-1 pt-1 text-[10px] tabular-nums text-muted-foreground"
                >
                  {getWeekNumber(cells[0].date)}
                </div>
              )}
              {cells.map((cell, di) => (
                <MonthCell
                  key={di}
                  date={cell.date}
                  isCurrentMonth={cell.isCurrentMonth}
                  isToday={cell.isToday}
                  events={cell.events}
                  maxVisible={monthViewMaxEvents === 0 ? Infinity : monthViewMaxEvents}
                  showEventTimes={showEventTimesInMonthView}
                  minHeightPx={minRowHeightPx}
                  isSelected={
                    !!selectedRange &&
                    startOfDay(cell.date).getTime() >= selectedRange[0] &&
                    startOfDay(cell.date).getTime() <= selectedRange[1]
                  }
                  onClickEvent={onClickEvent}
                  onShowMore={onShowMore}
                  dimPastEvents={dimPastEvents}
                  eventColorPriority={eventColorPriority}
                  onEventDragStart={onEventDragStart}
                  draggingEventId={draggingEventId}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
